/* Box rewards are a browser-persistent promotional reveal, not an entitlement server.
   Shopify discounts enforce monetary rewards; never put an Admin token in this file. */
(() => {
  'use strict';
  // v2: the four cards are exactly the 12-kit box gifts (no random bonus); v1 saves are dropped.
  const CAMPAIGN = 'box-gifts-v2';
  const KEY = 'lumi:' + CAMPAIGN;
  // Flip all four within 10 minutes of opening the page (the countdown); a finished set of gifts is then
  // saved for 48 hours from that first visit. An unfinished board past 10 minutes starts fresh.
  const FLIP_WINDOW = 10 * 60 * 1000;
  const TTL = 48 * 60 * 60 * 1000;
  const BONUSES = ['giftbox'];
  const FIXED = ['shipping', 'tools', 'board'];
  const LABELS = {
    shipping: { title: 'Free shipping', detail: 'To your doorstep, free', name: 'Free shipping' },
    tools: { title: 'Free custom tools ×2', detail: 'Easy for beginners', name: 'Free custom tools ×2' },
    board: { title: 'Free pressing board', detail: 'Make your clay anywhere', name: 'Free pressing board' },
    giftbox: { title: 'Free gift box', detail: 'Kits arrive with extra protection', name: 'Free gift box' }
  };
  const SVG = 'http://www.w3.org/2000/svg';
  // Line icons for the gifts that have no photo; strokes follow the card ink.
  const ICONS = {
    shipping: '<path d="M6 20h26v24H6z" fill="#FFCCD2"/><path d="M32 27h9l7 8v9H32z" fill="#FFF088"/><path d="M6 20h26v24H6zM32 27h9l7 8v9H32"/><circle cx="15" cy="45" r="4.5" fill="#FFF4DE"/><circle cx="40" cy="45" r="4.5" fill="#FFF4DE"/><path d="M1 26h8M3 32h7"/>'
  };
  let memoryState = null;
  const validState = value => value && value.campaign === CAMPAIGN && BONUSES.includes(value.bonus) &&
    Array.isArray(value.picks) && value.picks.length <= 4 && new Set(value.picks).size === value.picks.length &&
    value.picks.every(n => Number.isInteger(n) && n >= 0 && n < 9) &&
    Number.isFinite(value.startedAt) && Date.now() - value.startedAt < (value.picks.length === 4 ? TTL : FLIP_WINDOW);
  function readState() {
    try {
      const value = JSON.parse(localStorage.getItem(KEY));
      if (validState(value)) return value;
      if (value) localStorage.removeItem(KEY);
    } catch (_) { /* Storage may be unavailable in private mode. */ }
    return validState(memoryState) ? memoryState : (memoryState = null);
  }
  function saveState(value) {
    memoryState = value;
    try { localStorage.setItem(KEY, JSON.stringify(value)); return true; } catch (_) { return false; }
  }
  function randomBonus() {
    const data = new Uint32Array(1);
    crypto.getRandomValues(data);
    return BONUSES[data[0] % BONUSES.length];
  }
  function carryState(url, state) {
    const next = new URL(url, location.origin);
    if (state?.picks.length === 4) {
      // A non-secret presentation value also preserves the reveal when storage is blocked.
      next.searchParams.set('lumi_reward', state.bonus);
      next.searchParams.set('lumi_picks', state.picks.join(''));
      next.searchParams.set('lumi_t', String(state.startedAt));
    }
    const world = new URLSearchParams(location.search).get('world');
    if (world === 'wild' || world === 'jurassic') next.searchParams.set('world', world);
    return next.pathname + next.search;
  }
  function stateFromURL() {
    const q = new URLSearchParams(location.search);
    const bonus = q.get('lumi_reward'); const picks = q.get('lumi_picks');
    if (!BONUSES.includes(bonus) || !/^[0-8]{4}$/.test(picks || '')) return null;
    const value = { campaign: CAMPAIGN, bonus, picks: picks.split('').map(Number), startedAt: Number(q.get('lumi_t')) };
    if (!validState(value)) return null;
    saveState(value); return value;
  }
  const configFor = root => JSON.parse(root.querySelector('[data-lumi-gift-config]').textContent);
  const announce = (root, text) => { root.querySelector('[data-game-feedback]').textContent = text; };
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function prizeArt(key, config, world) {
    const art = document.createElement('span'); art.className = 'fw-prize-art';
    const img = (src, photo) => { const el = new Image(); el.src = src; el.alt = ''; el.decoding = 'async'; if (photo) el.className = 'is-photo'; return el; };
    if (ICONS[key]) {
      const svg = document.createElementNS(SVG, 'svg');
      svg.setAttribute('viewBox', '0 0 52 52'); svg.setAttribute('fill', 'none'); svg.setAttribute('stroke', '#30210B');
      svg.setAttribute('stroke-width', '2.4'); svg.setAttribute('stroke-linejoin', 'round'); svg.setAttribute('stroke-linecap', 'round');
      svg.innerHTML = ICONS[key]; art.append(svg);
    } else if (key === 'tools') art.append(img(config.images.tools, true));
    else if (key === 'board') art.append(img(config.images.board, true));
    else if (key === 'giftbox') { const box = img(config.images.giftbox, true); box.classList.add('is-fit'); art.append(box); }
    return art;
  }
  // One canvas drawn with requestAnimationFrame instead of ~90 animated elements, so the burst costs no layout
  // and no extra layers (the old version made the last card's flip stutter on phones).
  // The canvas is made ahead of time (prepConfetti, while the page is idle): creating a full-screen canvas at the
  // moment of the win was itself a dropped frame. Drawn at 1x, which confetti doesn't need sharper than.
  let confettiCanvas = null;
  function prepConfetti() {
    if (confettiCanvas || reducedMotion()) return confettiCanvas;
    const cv = document.createElement('canvas');
    cv.className = 'fw-confetti-canvas'; cv.setAttribute('aria-hidden', 'true');
    cv.width = innerWidth; cv.height = innerHeight;
    document.body.append(cv);
    cv.getContext('2d').clearRect(0, 0, 1, 1);
    return (confettiCanvas = cv);
  }
  function confetti() {
    const cv = prepConfetti();
    if (!cv) return;
    const W = innerWidth, H = innerHeight;
    if (cv.width !== W || cv.height !== H) { cv.width = W; cv.height = H; }
    const ctx = cv.getContext('2d');
    const colors = ['#F46A79', '#FFF088', '#C8E5FF', '#FF9E97', '#62B583', '#FFF4DE', '#7DDAFF', '#F7B661'];
    const bits = Array.from({ length: 80 }, (_, n) => {
      const size = 6 + Math.random() * 7;
      return {
        x: Math.random() * W, y: -20 - Math.random() * H * .35, w: size, h: size * (Math.random() > .5 ? 1.6 : 1),
        round: Math.random() > .5, c: colors[n % colors.length], vx: Math.random() * 1.6 - .8,
        vy: 1.5 + Math.random() * 2.5, g: .1 + Math.random() * .06, r: Math.random() * 6.3, vr: (Math.random() - .5) * .25, sway: Math.random() * 6.3,
      };
    });
    let last = performance.now();
    function frame(now) {
      const k = Math.min((now - last) / 16.7, 3); last = now; // frame-rate independent
      ctx.clearRect(0, 0, W, H);
      let alive = 0;
      for (const p of bits) {
        p.vy += p.g * k; p.y += p.vy * k; p.sway += .05 * k; p.x += (p.vx + Math.sin(p.sway) * .6) * k; p.r += p.vr * k;
        if (p.y > H + 20) continue;
        alive++;
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.r); ctx.fillStyle = p.c;
        if (p.round) { ctx.beginPath(); ctx.ellipse(0, 0, p.w / 2, p.h / 2, 0, 0, 6.2832); ctx.fill(); }
        else ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (alive) requestAnimationFrame(frame); else { cv.remove(); confettiCanvas = null; }
    }
    requestAnimationFrame(frame);
  }
  function initGame(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const config = configFor(root);
    // Warm the prize pictures so a flipped card shows its gift straight away, not a blank face while it downloads.
    root.warmed = ['tools', 'board', 'giftbox'].map(k => { const im = new Image(); im.src = config.images[k]; im.decode?.().catch(() => {}); return im; });
    const world = new URLSearchParams(location.search).get('world') === 'jurassic' ? 'jurassic' : 'wild';
    // ?reset starts a fresh game on this device (handy for testing); the flag is dropped from the URL afterwards.
    if (new URLSearchParams(location.search).has('reset')) {
      memoryState = null;
      try { localStorage.removeItem(KEY); } catch (_) { /* ignore */ }
      const clean = new URL(location.href); clean.searchParams.delete('reset'); history.replaceState(null, '', clean);
    }
    // The 48-hour window (and the countdown) starts on first visit, not first tap.
    let state = readState() || stateFromURL();
    if (!state) { state = { campaign: CAMPAIGN, bonus: randomBonus(), picks: [], startedAt: Date.now() }; saveState(state); }
    const cards = [...root.querySelectorAll('[data-card]')];
    const claim = root.querySelector('[data-claim]');
    const note = root.querySelector('[data-game-note]');
    let celebrated = state.picks.length === 4;
    if (!celebrated) setTimeout(() => (window.requestIdleCallback ? requestIdleCallback(prepConfetti, { timeout: 2000 }) : prepConfetti()), 1500);
    // holdDone: the 4th card is still turning, so keep the dimming, "You won" and the button pulse for when it lands.
    function paint(holdDone) {
      const count = state.picks.length;
      const rewards = [...FIXED, state.bonus];
      const full = count === 4;
      const done = full && !holdDone;
      root.querySelector('[data-won]').textContent = count + '/4';
      cards.forEach((card, i) => {
        const position = state.picks.indexOf(i);
        const revealed = position >= 0;
        card.disabled = revealed || full;
        card.setAttribute('aria-pressed', String(revealed));
        card.classList.toggle('is-locked', !revealed && done);
        if (revealed && !card.classList.contains('is-revealed')) {
          const key = rewards[position]; const reward = LABELS[key];
          const front = card.querySelector('.fw-card-front');
          const name = document.createElement('span'); name.className = 'fw-prize-name'; name.textContent = reward.name;
          const detail = document.createElement('span'); detail.className = 'fw-prize-mini'; detail.textContent = reward.mini || reward.detail;
          front.replaceChildren(prizeArt(key, config, world), name, detail);
          card.setAttribute('aria-label', 'Card ' + (i + 1) + ': ' + reward.title + '. ' + reward.detail);
          card.classList.add('is-revealed');
        }
      });
      claim.href = carryState(config.destination, state);
      claim.classList.toggle('is-active', done);
      claim.setAttribute('aria-disabled', String(!done));
      claim.textContent = done ? 'Claim my 4 gifts →' : 'Flip ' + (4 - count) + (count ? ' more' : '') + ' card' + (4 - count === 1 ? '' : 's');
      root.querySelector('[data-congrats]').hidden = !done;
      if (done) note.textContent = 'Saved on this phone. Your gifts will be waiting on the next page.';
    }
    cards.forEach((card, i) => card.addEventListener('click', () => {
      // Re-read before writing so a second tab cannot replace already selected cards.
      state = readState() || (validState(state) ? state : { campaign: CAMPAIGN, bonus: randomBonus(), picks: [], startedAt: Date.now() });
      if (state.picks.length >= 4 || state.picks.includes(i)) { paint(); return; }
      // Measured before the win message grows the page, which would move the bottom away from them.
      const wasAtBottom = atBottom();
      state.picks.push(i);
      if (state.picks.length === 4) state.doneAt = Date.now();
      const stored = saveState(state);
      const last = state.picks.length === 4 && !celebrated;
      paint(last);
      if (state.picks.length < 4) announce(root, LABELS[FIXED[state.picks.length - 1]].title + ' is yours. Flip ' + (4 - state.picks.length) + ' more.');
      else {
        announce(root, 'You won 4 gifts! ' + LABELS[state.bonus].title + '.');
        // Everything else waits for the flip (.7s) to land, so nothing competes with it for frames.
        // One job per frame so no single frame drops: confetti first, then the win state, then the countdown.
        if (!celebrated) {
          celebrated = true;
          const next = fn => new Promise(res => requestAnimationFrame(() => { fn(); res(); }));
          setTimeout(() => next(confetti).then(() => next(() => paint())).then(() => next(() => autoClaim(wasAtBottom))), reducedMotion() ? 0 : 680);
        }
        if (!stored) note.textContent = 'Use the button above to take your gifts with you.';
      }
    }));
    window.addEventListener('storage', event => { if (event.key === KEY) { state = readState() || state; paint(); } });

    // Like Grüns: once the fourth card lands, count down and carry the gifts to the product page.
    // Only right after winning, so a returning visitor can still read their cards or leave a review.
    // The visitor stays put if they touch the review box or scroll to the bottom of the page.
    const SECONDS = 4;
    const BOTTOM_SLACK = 40; // px; covers iOS toolbar resizing and sub-pixel scroll positions
    function atBottom() {
      const doc = document.scrollingElement || document.documentElement;
      const viewport = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      // A page that fits on screen is "at the bottom" without the visitor doing anything; ignore it.
      if (doc.scrollHeight - viewport <= BOTTOM_SLACK) return false;
      return doc.scrollTop + viewport >= doc.scrollHeight - BOTTOM_SLACK;
    }
    function autoClaim(startedAtBottom) {
      const review = root.querySelector('.fw-review');
      let left = SECONDS;
      let timer = 0;
      let done = false;
      const label = () => {
        claim.textContent = 'Claim my 4 gifts → ' + left;
        note.textContent = 'Taking you to your gifts. Scroll down or tap the review box to stay here.';
      };
      function stop() {
        if (done) return;
        done = true;
        clearInterval(timer);
        window.removeEventListener('scroll', onScroll);
        document.removeEventListener('visibilitychange', onVisibility);
        ['pointerdown', 'touchstart', 'focusin', 'keydown'].forEach(type => review?.removeEventListener(type, onReview, true));
        claim.removeEventListener('click', onClaim);
      }
      function cancel() {
        if (done) return;
        stop();
        paint();
        announce(root, 'Staying on this page. Tap Claim my 4 gifts when you are ready.');
      }
      function go() { stop(); location.assign(claim.href); }
      function tick() {
        if (document.hidden) return; // a background tab should not jump pages when the visitor comes back
        left -= 1;
        if (left > 0) label(); else go();
      }
      function onScroll() { if (atBottom()) cancel(); }
      function onReview() { cancel(); }
      function onVisibility() { if (!document.hidden && atBottom()) cancel(); }
      function onClaim() { stop(); }
      if (startedAtBottom || atBottom()) { announce(root, 'Tap Claim my 4 gifts when you are ready.'); return; }
      label();
      announce(root, 'You won 4 gifts! Taking you to them in ' + SECONDS + ' seconds.');
      timer = setInterval(tick, 1000);
      window.addEventListener('scroll', onScroll, { passive: true });
      document.addEventListener('visibilitychange', onVisibility);
      // Capture phase so a tap on the stars, fields or button counts before any other handler.
      ['pointerdown', 'touchstart', 'focusin', 'keydown'].forEach(type => review?.addEventListener(type, onReview, { capture: true, passive: true }));
      claim.addEventListener('click', onClaim);
    }
    // Coming back from the product page via the back button restores this page from cache mid-countdown.
    window.addEventListener('pageshow', event => { if (event.persisted) { state = readState() || state; paint(); } });

    // Idle life: cards float after dealing in, and every few seconds one unflipped card wiggles for attention.
    setTimeout(() => cards.forEach(card => card.classList.add('is-float')), 900);
    setInterval(() => {
      if (document.hidden || reducedMotion() || state.picks.length === 4) return;
      const idle = cards.filter(card => !card.disabled);
      const pick = idle[Math.floor(Math.random() * idle.length)];
      if (!pick) return;
      pick.classList.remove('is-nudge'); void pick.offsetWidth; pick.classList.add('is-nudge');
      setTimeout(() => pick.classList.remove('is-nudge'), 900);
    }, 2600);

    // Countdown to the end of the 10-minute flip window. It stops once all four are flipped (the gifts are
    // then saved); if it runs out first, the page starts fresh.
    const pad = n => String(n).padStart(2, '0');
    const [m, s] = ['m', 's'].map(k => root.querySelector('[data-timer-' + k + ']'));
    function tick() {
      const done = state.picks.length === 4;
      const left = Math.max(0, state.startedAt + FLIP_WINDOW - (done ? (state.doneAt || state.startedAt + FLIP_WINDOW) : Date.now()));
      const secs = Math.floor(left / 1000);
      m.textContent = pad(Math.floor(secs / 60)); s.textContent = pad(secs % 60);
      if (!left && !done) { try { localStorage.removeItem(KEY); } catch (_) { /* ignore */ } location.replace(location.pathname + (world === 'jurassic' ? '?world=jurassic' : '?world=wild')); }
    }
    tick(); setInterval(tick, 1000);
    paint();
    initReview(root, world);
  }
  function initReview(root, world) {
    const form = root.querySelector('.fw-review-form');
    if (!form) return;
    const kit = form.querySelector('[data-review-kit]');
    if (kit) kit.value = world === 'jurassic' ? 'Little Jurassic' : 'Into the Wild';
    if (world === 'jurassic') form.querySelectorAll('img[data-jurassic]').forEach(img => { img.src = img.dataset.jurassic; });
    const q = new URLSearchParams(location.search);
    if (q.get('review') === 'sent' || q.get('contact_posted') === 'true') {
      form.querySelector('[data-review-done]').hidden = false;
      form.querySelector('[data-review-start]').hidden = true;
    }
    // Come back to this page (same world) with a success flag after Shopify accepts the message.
    const back = document.createElement('input');
    back.type = 'hidden'; back.name = 'return_to'; back.value = location.pathname + '?world=' + world + '&review=sent#lumi-review';
    form.append(back);
    const hasErrors = !!form.querySelector('.fw-review-error');
    if (!hasErrors) form.classList.add('is-collapsed');
    const labels = [...form.querySelectorAll('.fw-stars label')];
    const light = value => labels.forEach((label, i) => label.classList.toggle('is-lit', i < value));
    form.querySelectorAll('.fw-stars input').forEach((input, i) => {
      input.addEventListener('change', () => {
        light(i + 1);
        if (form.classList.contains('is-collapsed')) {
          form.classList.remove('is-collapsed');
          setTimeout(() => form.querySelector('textarea')?.focus({ preventScroll: true }), 50);
          form.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
        }
      });
      labels[i].addEventListener('mouseenter', () => light(i + 1));
      labels[i].addEventListener('mouseleave', () => light(Number(form.querySelector('.fw-stars input:checked')?.value.charAt(0) || 0)));
    });
  }
  async function cartRequest(path, data) {
    const response = await fetch((window.Shopify?.routes?.root || '/') + path, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(data) });
    const body = await response.json();
    if (!response.ok || body.status >= 400) throw new Error(body.description || body.message || 'Please try again in a moment.');
    return body;
  }
  function initPDP(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const config = configFor(root);
    let state = readState() || stateFromURL();
    let selected = config.products.find(p => p.handle && p.handle === root.dataset.currentHandle) || config.products.find(p => p.world === new URLSearchParams(location.search).get('world')) || config.products[0];
    const quantity = root.querySelector('[data-quantity]');
    const buy = root.querySelector('[data-add-to-cart]');
    const money = value => new Intl.NumberFormat('en-AU', { style: 'currency', currency: config.currency }).format(value / 100);
    const clamp = () => { quantity.value = Math.max(1, Math.min(10, Math.floor(Number(quantity.value) || 1))); return Number(quantity.value); };
    const hasRewards = () => state?.picks.length === 4;
    const isReady = () => config.ready && selected.variant && selected.available;
    function hero() {
      root.querySelector('[data-main-photo]').src = selected.image;
      root.querySelector('[data-main-photo]').alt = selected.character + ' clay character with the ' + selected.title + ' clay kit';
      root.querySelector('[data-photo="hero"] img').src = selected.image;
      root.querySelectorAll('[data-photo]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.photo === 'hero')));
    }
    function paint() {
      root.querySelector('[data-product-title]').textContent = selected.productTitle || selected.title;
      root.querySelector('[data-world-number]').textContent = selected.world === 'wild' ? '01 / 02' : '02 / 02';
      root.querySelectorAll('[data-world]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.world === selected.world)));
      root.querySelector('[data-price]').textContent = selected.variant ? money(selected.price) : 'Coming soon';
      root.querySelector('[data-price-label]').textContent = selected.variant ? 'AUD · ' + (selected.available ? 'Ready for your next clay day' : 'Currently unavailable') : 'Something lovely is taking shape.';
      root.querySelector('[data-launch-note]').hidden = !!isReady();
      buy.disabled = !isReady();
      root.querySelector('[data-buy-label]').textContent = isReady() ? (hasRewards() ? 'Add to bag + my free gifts' : 'Add to bag') : 'New collection coming soon';
      const note = root.querySelector('[data-reward-note]');
      if (hasRewards()) {
        root.querySelector('[data-ticket-title]').textContent = 'Your 4 rewards are coming too';
        root.querySelector('[data-ticket-badge]').textContent = 'UNLOCKED';
        root.querySelector('[data-bonus-label]').textContent = LABELS[state.bonus].title;
        root.querySelector('[data-bonus-value]').textContent = 'FREE';
        note.textContent = isReady() ? 'Added automatically with your purchase. Your discount carries through to checkout.' : 'Saved for you. We’ll add these when the new collection opens.';
      }
      root.querySelector('[data-purchase-note]').textContent = isReady() && hasRewards() ? 'Your gifts and discount are included at checkout.' : 'Good things are worth making.';
      hero();
    }
    root.querySelectorAll('[data-world]').forEach(button => button.addEventListener('click', () => {
      selected = config.products.find(p => p.world === button.dataset.world); paint();
      const url = new URL(location.href); url.searchParams.set('world', selected.world); history.replaceState(null, '', url);
    }));
    root.querySelectorAll('[data-photo]').forEach(button => button.addEventListener('click', () => {
      if (button.dataset.photo === 'hero') { hero(); return; }
      root.querySelector('[data-main-photo]').src = button.dataset.photo;
      root.querySelector('[data-main-photo]').alt = button.dataset.photoAlt;
      root.querySelectorAll('[data-photo]').forEach(b => b.setAttribute('aria-pressed', String(b === button)));
    }));
    root.querySelector('[data-quantity-minus]').addEventListener('click', () => { quantity.value = clamp() - 1; clamp(); });
    root.querySelector('[data-quantity-plus]').addEventListener('click', () => { quantity.value = clamp() + 1; clamp(); });
    quantity.addEventListener('change', clamp);
    buy.addEventListener('click', async () => {
      if (!isReady()) return;
      const error = root.querySelector('[data-purchase-error]'); error.hidden = true;
      root.querySelector('[data-cart-success]').hidden = true;
      buy.disabled = true; root.querySelector('[data-buy-label]').textContent = 'Adding a little joy…';
      try {
        const items = [{ id: selected.variant, quantity: clamp() }];
        const codes = [];
        if (hasRewards()) {
          if (!config.toolsVariant || !config.boardVariant || !config.shippingCode || !config.giftCode) throw new Error('Your gifts aren’t quite ready. Please contact us and we’ll help.');
          codes.push(config.shippingCode, config.giftCode);
          const giftProperties = { _lumi_campaign: CAMPAIGN, 'Box reward': 'Complimentary with your clay order' };
          const cartResponse = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { credentials: 'same-origin' });
          if (!cartResponse.ok) throw new Error('We couldn’t check your bag. Please try again.');
          const cart = await cartResponse.json();
          const desired = [{ id: config.toolsVariant, quantity: 1 }, { id: config.boardVariant, quantity: 1 }];
          for (const gift of desired) {
            const existing = cart.items.filter(line => line.variant_id === gift.id && line.properties?._lumi_campaign === CAMPAIGN).reduce((sum, line) => sum + line.quantity, 0);
            if (gift.quantity > existing) items.push({ id: gift.id, quantity: gift.quantity - existing, properties: giftProperties });
          }
        }
        // Apply the discounts first so a failed discount request cannot leave full-priced gifts in the bag.
        if (codes.length) {
          const discounted = await cartRequest('cart/update.js', { discount: codes.join(','), attributes: { lumi_box_reward: state.bonus, lumi_box_campaign: CAMPAIGN } });
          if (discounted.errors) throw new Error('Your reward could not be applied. Please try again.');
        }
        await cartRequest('cart/add.js', { items });
        // Shopify resolves discount eligibility after merchandise has been added.
        if (codes.length) await cartRequest('cart/update.js', { discount: codes.join(',') });
        const cartURL = (window.Shopify?.routes?.root || '/') + 'cart';
        location.assign(codes.length ? '/discount/' + encodeURIComponent(codes.join(',')) + '?redirect=' + encodeURIComponent(cartURL) : cartURL);
      } catch (failure) {
        error.textContent = failure.message || 'Something went wrong. Please try again.'; error.hidden = false;
        // Never automatically retry a cart add: it may have succeeded before a lost response.
        error.append(' If your bag changed, review it before trying again.');
        const link = document.createElement('a'); link.href = '/cart'; link.textContent = ' View bag'; error.append(link);
        buy.disabled = !isReady(); root.querySelector('[data-buy-label]').textContent = 'Add to bag' + (hasRewards() ? ' + my free gifts' : '');
      }
    });
    window.addEventListener('storage', event => { if (event.key === KEY) { state = readState(); paint(); } });
    paint();
  }
  function boot() { document.querySelectorAll('[data-lumi-game]').forEach(initGame); document.querySelectorAll('[data-lumi-pdp]').forEach(initPDP); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
